import React, { useState, useEffect, useCallback, useRef } from "react";
import { permitService } from "../../_Shared/Scripts/Endpoints";
import SuccessAnimation from "../../_Shared/Scripts/SuccessAnimation";

const LockedPmcForm = ({ lang, onSuccess, pmcId, onSubmit }) => {
  const [pending, setPending] = useState(false);
  const [code, setCode] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);
  const [success, setSuccess] = useState(false);
  const timeoutRef = useRef(null);
  
  const codeText = lang == "sv" ? "Kod" : lang == "fi" ? "Koodi" : lang == "nb" ? "Kode" : "Code";
  const codeIsRequiredText = lang == "sv" ? "Kod krävs" : lang == "fi" ? "Koodi on pakollinen" : lang == "nb" ? "Kode kreves" : "Code is required";
  const codeIsTooShortText = lang == "sv" ? "Koden är för kort (minst 4 tecken)" : lang == "fi" ? "Koodi on liian lyhyt (vähintään 4 merkkiä)" : lang == "nb" ? "Koden er for kort (minst 4 tegn)" : "The code is too short (minimum 4 characters)";
  const invalidCodeRegexText = lang == "sv" ? "Ogiltig kod. Endast bokstäver, siffror, '-' och '' är tillåtna" : lang == "fi" ? "Virheellinen koodi. Vain kirjaimet, numerot, '-' ja '_' ovat sallittuja." : lang == "nb" ? "Ugyldig kode. Bare bokstaver, tall, '-' og '' er tillatt." : "Invalid code. Only letters, numbers, '-' and '_' are allowed";
  const invalidCodeText = lang == "sv" ? "Ogiltig kod. Försök med en annan kod." : lang == "fi" ? "Virheellinen koodi. Kokeile toista koodia." : lang == "nb" ? "Ugyldig kode. Prøv en annen kode." : "Invalid code. Try another code.";
  const submitText = lang == "sv" ? "Skicka" : lang == "fi" ? "Lähetä" : lang == "nb" ? "Sende" : "Submit";
  const submitingText = lang == "sv" ? "Skickar..." : lang == "fi" ? "Lähetetään..." : lang == "nb" ? "Sender..." : "Submiting...";
  const maxLimitReachedText = lang == "sv" ? "Den här koden har inga tillgängliga användningsområden kvar." : lang == "fi" ? "Annetussa koodissa ei ole enää käyttökertoja jäljellä." : lang == "nb" ? "Denne koden har ingen tilgjengelige bruksområder igjen." : "This code has no available uses left.";
  
  const validateCode = useCallback((value) => {
    const trimmed = value.trim();

    if (!trimmed) {
        return codeIsRequiredText;
    }

    if (trimmed.length < 4) {
        return codeIsTooShortText;
    }

    const codeRegex = /^[A-Za-z0-9_-]+$/; 
    if (!codeRegex.test(trimmed)) {
        return invalidCodeRegexText;
    }

    return ""; 
  }, []);

  const resetForm = useCallback(() => {
    setCode("");
    setSuccess(false);
    setErrors({});
    setTouched(false);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setTouched(true);
    const codeError = validateCode(code);
    if (codeError) return setErrors({ code: codeError });

    try {
        setPending(true);
        const getUnlockedConfigs = await permitService.getUnlockedConfigs({
                pmcId,
                "unlockCode": code
            }, false);

        if (getUnlockedConfigs.status !== 200) {
            const msg = getUnlockedConfigs.body?.StatusDescription == 'Invalid code'  ? invalidCodeText : "No configs returned. Please try again.";
            setErrors({ common: msg });
            return;
        }
    
        const data = getUnlockedConfigs.body.content;

        if (data.numberOfCodes > data.numberOfUsedCodes) {
            setSuccess(true);

            localStorage.setItem("validUnlockCode", code);
            localStorage.setItem("validUnlockPmcId", pmcId);

            onSubmit(); 

            timeoutRef.current = setTimeout(() => {
                onSuccess();
                resetForm();
            }, 1400);

        } else {
            console.log("Code exists but has no available uses left.");
            setErrors({ common: maxLimitReachedText });
        }

    } catch (error) {
      setErrors({ common: "Unable to check the code. Please try again!" });
    } finally {
      setPending(false);
    }
  }, [code, onSuccess, resetForm, validateCode]);

  const handleCodeChange = useCallback((e) => {
    const value = e.target.value;
    setCode(value);
    if (touched) {
        const message = validateCode(value);
        setErrors({ code: message });
    }
  }, [touched, validateCode]);

  const handleBlur = useCallback(() => {
    setTouched(true);
    const message = validateCode(code);
    setErrors({ code: message });
  }, [code, validateCode]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {!success ? (
        <form 
          className="pmc-locked-form"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="input-container">
            <label htmlFor="code-input" className="code-form__label">
              {codeText}
            </label>
            <input
              id="code-input"
              type="text"
              value={code}
              onChange={handleCodeChange}
              onBlur={handleBlur}
              className="code-form__input"
              placeholder=""
              disabled={pending}
              autoComplete="code"
            />
          </div>
          
          {errors.code && touched && (
            <span className="form-error">{errors.code}</span>
          )}
          {errors.common && (
            <span className="form-error">{errors.common}</span>
          )}

          <div className="pmc-locked-form__buttons">
            <button 
              type="submit" 
              className="btn btn--save"
              disabled={pending}
            >
              {pending ? submitingText : submitText}
            </button>
          </div>
        </form>
      ) : (
        <div className="success-message">
          <SuccessAnimation />
        </div>
      )}
    </div>
  );
};

export default React.memo(LockedPmcForm);
